import { xchacha20poly1305Decrypt, xchacha20poly1305Encrypt } from './aead.js';
import { ed25519Sign, ed25519Verify } from './ed25519.js';
import { FumeCryptoError } from './errors.js';
import { sha256 } from './hash.js';
import { ED25519_PUBLIC_KEY_SIZE, ED25519_SIGNATURE_SIZE, XCHACHA20POLY1305_KEY_SIZE, XCHACHA20POLY1305_NONCE_SIZE, XCHACHA20POLY1305_TAG_SIZE, } from './constants.js';
import { bytesEqual, concatBytes, requireLength, uint64be } from './internal.js';
export const RECEIPT_VERSION = 1;
export const RECEIPT_ID_SIZE = 32;
export const RECEIPT_NONCE_SIZE = XCHACHA20POLY1305_NONCE_SIZE;
export const RECEIPT_TAG_SIZE = XCHACHA20POLY1305_TAG_SIZE;
export const RECEIPT_KINDS = ['sent', 'received', 'read', 'expired', 'destroyed'];
export class ReceiptError extends Error {
    constructor(message) {
        super(message);
        this.name = 'ReceiptError';
    }
}
const SESSION_ID_SIZE = 32;
const TRANSCRIPT_HASH_SIZE = 32;
const HEADER_SIZE = 4 + RECEIPT_ID_SIZE + RECEIPT_NONCE_SIZE;
const MIN_SEALED_SIZE = HEADER_SIZE + RECEIPT_TAG_SIZE;
const RECEIPT_DOMAIN = new TextEncoder().encode('fume.receipt.v1');
const RECEIPT_SEAL_AD_PREFIX = new TextEncoder().encode('fume.receipt.seal.v1');
const ZERO_RECEIPT_ID = new Uint8Array(RECEIPT_ID_SIZE);
function wrapCryptoError(err) {
    if (err instanceof ReceiptError)
        throw err;
    if (err instanceof FumeCryptoError)
        throw new ReceiptError(err.message);
    if (err instanceof Error)
        throw new ReceiptError(err.message);
    throw new ReceiptError('receipt operation failed');
}
function bytesHex(bytes) {
    return Buffer.from(bytes).toString('hex');
}
function uint32be(x) {
    assertIntegerRange(x, 0xffffffff, 'uint32');
    const out = new Uint8Array(4);
    new DataView(out.buffer).setUint32(0, x, false);
    return out;
}
function readUint32be(bytes, offset) {
    if (offset + 4 > bytes.length)
        throw new ReceiptError('sealed receipt is truncated');
    return new DataView(bytes.buffer, bytes.byteOffset + offset, 4).getUint32(0, false);
}
function readUint64be(bytes, name) {
    if (bytes.length !== 8)
        throw new ReceiptError(`${name} frame must contain uint64be`);
    const value = new DataView(bytes.buffer, bytes.byteOffset, 8).getBigUint64(0, false);
    if (value > BigInt(Number.MAX_SAFE_INTEGER)) {
        throw new ReceiptError(`${name} exceeds Number.MAX_SAFE_INTEGER`);
    }
    return Number(value);
}
function assertIntegerRange(value, max, name) {
    if (!Number.isInteger(value) || value < 0 || value > max) {
        throw new ReceiptError(`${name} must be an integer in 0..${max}`);
    }
}
function validateBytes(value, expected, name) {
    try {
        requireLength(value, expected, name);
    }
    catch (err) {
        wrapCryptoError(err);
    }
}
function validateRole(role) {
    if (role !== 'initiator' && role !== 'responder') {
        throw new ReceiptError(`role must be 'initiator' or 'responder'`);
    }
}
function validateKind(kind) {
    if (!RECEIPT_KINDS.includes(kind)) {
        throw new ReceiptError(`kind must be one of ${RECEIPT_KINDS.join(', ')}`);
    }
}
function validateContext(context) {
    if (context === undefined || context === null) {
        throw new ReceiptError('context is required');
    }
    validateBytes(context.sessionId, SESSION_ID_SIZE, 'context.sessionId');
    validateBytes(context.transcriptHash, TRANSCRIPT_HASH_SIZE, 'context.transcriptHash');
    validateBytes(context.deviceKeyA, ED25519_PUBLIC_KEY_SIZE, 'context.deviceKeyA');
    validateBytes(context.deviceKeyB, ED25519_PUBLIC_KEY_SIZE, 'context.deviceKeyB');
    if (bytesEqual(context.deviceKeyA, context.deviceKeyB)) {
        throw new ReceiptError('context device keys must be distinct');
    }
    assertIntegerRange(context.ttlSeconds, Number.MAX_SAFE_INTEGER, 'context.ttlSeconds');
}
function frame(bytes) {
    return concatBytes(uint64be(bytes.length), bytes);
}
function utf8Bytes(value) {
    return new TextEncoder().encode(value);
}
function canonicalReceiptBody(fields) {
    validateRole(fields.role);
    validateKind(fields.kind);
    assertIntegerRange(fields.ttlSeconds, Number.MAX_SAFE_INTEGER, 'ttlSeconds');
    assertIntegerRange(fields.counter, Number.MAX_SAFE_INTEGER, 'counter');
    validateBytes(fields.sessionId, SESSION_ID_SIZE, 'sessionId');
    validateBytes(fields.transcriptHash, TRANSCRIPT_HASH_SIZE, 'transcriptHash');
    validateBytes(fields.recipientDeviceKey, ED25519_PUBLIC_KEY_SIZE, 'recipientDeviceKey');
    validateBytes(fields.prevReceiptId, RECEIPT_ID_SIZE, 'prevReceiptId');
    return concatBytes(frame(RECEIPT_DOMAIN), frame(fields.sessionId), frame(fields.transcriptHash), frame(uint64be(fields.ttlSeconds)), frame(utf8Bytes(fields.role)), frame(utf8Bytes(fields.kind)), frame(uint64be(fields.counter)), frame(fields.recipientDeviceKey), frame(fields.prevReceiptId));
}
function readFrame(body, offset) {
    if (offset + 8 > body.length)
        throw new ReceiptError('receipt body is truncated');
    const lengthBig = new DataView(body.buffer, body.byteOffset + offset, 8).getBigUint64(0, false);
    if (lengthBig > BigInt(Number.MAX_SAFE_INTEGER)) {
        throw new ReceiptError('receipt body frame length exceeds Number.MAX_SAFE_INTEGER');
    }
    const length = Number(lengthBig);
    const start = offset + 8;
    const end = start + length;
    if (end > body.length)
        throw new ReceiptError('receipt body is truncated');
    return { value: body.slice(start, end), offset: end };
}
function parseCanonicalBody(body) {
    if (!(body instanceof Uint8Array))
        throw new ReceiptError('receipt body must be a Uint8Array');
    let offset = 0;
    const domain = readFrame(body, offset);
    offset = domain.offset;
    if (!bytesEqual(domain.value, RECEIPT_DOMAIN)) {
        throw new ReceiptError('receipt body has an invalid domain');
    }
    const sessionId = readFrame(body, offset);
    offset = sessionId.offset;
    const transcriptHash = readFrame(body, offset);
    offset = transcriptHash.offset;
    const ttlSeconds = readFrame(body, offset);
    offset = ttlSeconds.offset;
    const role = readFrame(body, offset);
    offset = role.offset;
    const kind = readFrame(body, offset);
    offset = kind.offset;
    const counter = readFrame(body, offset);
    offset = counter.offset;
    const recipientDeviceKey = readFrame(body, offset);
    offset = recipientDeviceKey.offset;
    const prevReceiptId = readFrame(body, offset);
    offset = prevReceiptId.offset;
    if (offset !== body.length)
        throw new ReceiptError('receipt body has trailing bytes');
    const parsedRole = new TextDecoder('utf-8', { fatal: true }).decode(role.value);
    const parsedKind = new TextDecoder('utf-8', { fatal: true }).decode(kind.value);
    const parsed = {
        body,
        sessionId: sessionId.value,
        transcriptHash: transcriptHash.value,
        ttlSeconds: readUint64be(ttlSeconds.value, 'ttlSeconds'),
        role: parsedRole,
        kind: parsedKind,
        counter: readUint64be(counter.value, 'counter'),
        recipientDeviceKey: recipientDeviceKey.value,
        prevReceiptId: prevReceiptId.value,
    };
    const recanonicalized = canonicalReceiptBody(parsed);
    if (!bytesEqual(recanonicalized, body)) {
        throw new ReceiptError('receipt body is not canonical');
    }
    return parsed;
}
function receiptAD(context) {
    return concatBytes(RECEIPT_SEAL_AD_PREFIX, context.sessionId, context.transcriptHash);
}
function nonceFromOptions(options) {
    if (!options?.deterministicNonce)
        return undefined;
    const nonce = options.deterministicNonce();
    validateBytes(nonce, RECEIPT_NONCE_SIZE, 'deterministicNonce()');
    return nonce;
}
function signerCandidate(context, role, kind, counter, signerSecretKey, prevReceiptId, signerPublicKey, recipientDeviceKey) {
    const body = canonicalReceiptBody({
        sessionId: context.sessionId,
        transcriptHash: context.transcriptHash,
        ttlSeconds: context.ttlSeconds,
        role,
        kind,
        counter,
        recipientDeviceKey,
        prevReceiptId,
    });
    let signature;
    try {
        signature = ed25519Sign(body, signerSecretKey);
    }
    catch (err) {
        wrapCryptoError(err);
    }
    if (!ed25519Verify(body, signature, signerPublicKey))
        return undefined;
    return { body, signature, receiptId: sha256(body) };
}
function resolveSigner(body, signature, context, recipientDeviceKey) {
    const signedByA = ed25519Verify(body, signature, context.deviceKeyA);
    const signedByB = ed25519Verify(body, signature, context.deviceKeyB);
    if (signedByA === signedByB) {
        throw new ReceiptError('receipt signature is invalid or signer is not committed in T');
    }
    if (signedByA) {
        if (!bytesEqual(recipientDeviceKey, context.deviceKeyB)) {
            throw new ReceiptError('recipientDeviceKey must be the other T-committed key');
        }
        return context.deviceKeyA.slice();
    }
    if (!bytesEqual(recipientDeviceKey, context.deviceKeyA)) {
        throw new ReceiptError('recipientDeviceKey must be the other T-committed key');
    }
    return context.deviceKeyB.slice();
}
export function buildReceipt(params) {
    try {
        validateContext(params.context);
        validateKind(params.kind);
        validateRole(params.role);
        assertIntegerRange(params.counter, Number.MAX_SAFE_INTEGER, 'counter');
        validateBytes(params.K_receipt, XCHACHA20POLY1305_KEY_SIZE, 'K_receipt');
        const prevReceiptId = params.prevReceiptId ?? ZERO_RECEIPT_ID;
        validateBytes(prevReceiptId, RECEIPT_ID_SIZE, 'prevReceiptId');
        if (!(params.signerSecretKey instanceof Uint8Array)) {
            throw new ReceiptError('signerSecretKey must be a Uint8Array');
        }
        const fromA = signerCandidate(params.context, params.role, params.kind, params.counter, params.signerSecretKey, prevReceiptId, params.context.deviceKeyA, params.context.deviceKeyB);
        const fromB = signerCandidate(params.context, params.role, params.kind, params.counter, params.signerSecretKey, prevReceiptId, params.context.deviceKeyB, params.context.deviceKeyA);
        if ((fromA === undefined) === (fromB === undefined)) {
            throw new ReceiptError('signerSecretKey does not match exactly one T-committed device key');
        }
        const selected = fromA ?? fromB;
        const nonce = nonceFromOptions(params.options);
        const box = xchacha20poly1305Encrypt(concatBytes(selected.body, selected.signature), receiptAD(params.context), params.K_receipt, nonce);
        const sealed = concatBytes(uint32be(RECEIPT_VERSION), selected.receiptId, box.nonce, box.ciphertext);
        return { receiptId: selected.receiptId, sealed };
    }
    catch (err) {
        wrapCryptoError(err);
    }
}
export function openReceipt(sealed, K_receipt, context) {
    try {
        if (!(sealed instanceof Uint8Array))
            throw new ReceiptError('sealed receipt must be a Uint8Array');
        validateBytes(K_receipt, XCHACHA20POLY1305_KEY_SIZE, 'K_receipt');
        validateContext(context);
        if (sealed.length < MIN_SEALED_SIZE)
            throw new ReceiptError('sealed receipt is too short');
        const version = readUint32be(sealed, 0);
        if (version !== RECEIPT_VERSION) {
            throw new ReceiptError(`unsupported receipt version ${version}`);
        }
        const headerReceiptId = sealed.slice(4, 4 + RECEIPT_ID_SIZE);
        const nonce = sealed.slice(4 + RECEIPT_ID_SIZE, HEADER_SIZE);
        const ciphertext = sealed.slice(HEADER_SIZE);
        const plaintext = xchacha20poly1305Decrypt(ciphertext, receiptAD(context), K_receipt, nonce);
        if (plaintext.length < ED25519_SIGNATURE_SIZE) {
            throw new ReceiptError('receipt plaintext is too short');
        }
        const body = plaintext.slice(0, plaintext.length - ED25519_SIGNATURE_SIZE);
        const signature = plaintext.slice(plaintext.length - ED25519_SIGNATURE_SIZE);
        const parsed = parseCanonicalBody(body);
        const computedReceiptId = sha256(body);
        if (!bytesEqual(computedReceiptId, headerReceiptId)) {
            throw new ReceiptError('receiptId does not match canonical body');
        }
        if (!bytesEqual(parsed.sessionId, context.sessionId) ||
            !bytesEqual(parsed.transcriptHash, context.transcriptHash) ||
            parsed.ttlSeconds !== context.ttlSeconds) {
            throw new ReceiptError('receipt context binding does not match supplied context');
        }
        const signerPublicKey = resolveSigner(body, signature, context, parsed.recipientDeviceKey);
        return {
            version: RECEIPT_VERSION,
            receiptId: headerReceiptId,
            sessionId: parsed.sessionId,
            transcriptHash: parsed.transcriptHash,
            ttlSeconds: parsed.ttlSeconds,
            role: parsed.role,
            kind: parsed.kind,
            counter: parsed.counter,
            signerPublicKey,
            recipientDeviceKey: parsed.recipientDeviceKey,
            prevReceiptId: parsed.prevReceiptId,
            signature,
        };
    }
    catch (err) {
        wrapCryptoError(err);
    }
}
export function verifyReceiptChain(sealedList, K_receipt, context) {
    if (!Array.isArray(sealedList))
        throw new ReceiptError('sealedList must be an array');
    const events = [];
    const seenReceiptIds = new Set();
    const lastCounterBySigner = new Map();
    for (let i = 0; i < sealedList.length; i++) {
        const event = openReceipt(sealedList[i], K_receipt, context);
        const receiptIdHex = bytesHex(event.receiptId);
        if (seenReceiptIds.has(receiptIdHex)) {
            throw new ReceiptError('receipt chain contains duplicate receiptId');
        }
        seenReceiptIds.add(receiptIdHex);
        const expectedPrev = i === 0 ? ZERO_RECEIPT_ID : events[i - 1].receiptId;
        if (!bytesEqual(event.prevReceiptId, expectedPrev)) {
            throw new ReceiptError('receipt prevReceiptId does not link to previous event');
        }
        const signerHex = bytesHex(event.signerPublicKey);
        const lastCounter = lastCounterBySigner.get(signerHex);
        if (lastCounter !== undefined && event.counter <= lastCounter) {
            throw new ReceiptError('receipt counter must be strictly increasing per signer');
        }
        lastCounterBySigner.set(signerHex, event.counter);
        events.push(event);
    }
    return { events };
}
//# sourceMappingURL=receipt.js.map